/**
 * Display vocabulary for the order book — status pills, dates, and the
 * wireframe's money style.
 */
import type { StatusTone } from "@flint/ui";

export const ORDER_STATUS_LABEL = {
  draft: "Draft",
  confirmed: "Confirmed",
  approved: "Approved",
  fulfilled: "Fulfilled",
  rejected: "Rejected",
} as const;

/**
 * Tones follow the deck: draft muted, confirmed amber (needs attention),
 * approved/fulfilled positive, rejected red. "Past dispatch" overrides all of
 * them with `bad` — it's the one thing an operator must act on today.
 */
export const ORDER_STATUS_TONE = {
  draft: "muted",
  confirmed: "warn",
  approved: "sky",
  fulfilled: "ok",
  rejected: "bad",
} as const satisfies Record<string, StatusTone>;

export type OrderStatus = keyof typeof ORDER_STATUS_LABEL;

/**
 * "₹ 84,200" — the list style from the wireframe: a space after the symbol,
 * Indian digit grouping, and **no decimals**. Order values are large enough
 * that paise are noise in a scan-the-column context; the detail page's unit
 * prices keep them.
 */
export function orderMoney(paise: string): string {
  const rupees = Number(BigInt(paise) / 100n);
  return `₹ ${rupees.toLocaleString("en-IN")}`;
}

/** "₹ 245.00" — unit prices and line totals, where the paise matter. */
export function exactMoney(paise: string): string {
  const n = BigInt(paise);
  const whole = n / 100n;
  const frac = (n % 100n).toString().padStart(2, "0");
  return `₹ ${Number(whole).toLocaleString("en-IN")}.${frac}`;
}

/**
 * "28 Jul" — the deck's short form for a **timestamp** in a table.
 *
 * Rendered in the viewer's zone on purpose: `createdAt` and `sentAt` are
 * moments, and "when was this raised" is a question about the reader's clock.
 *
 * Do **not** use this for a date-only column (`dispatchBy`, `expiresOn`, a
 * delivery window). Those arrive as `yyyy-mm-dd`, which parses to UTC midnight,
 * so the viewer's zone shifts them a day west of UTC — `shortDay` in
 * `../date-window.ts` pins them to UTC and is the one to call.
 */
export function shortDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short" });
}
